import { resolveInstallmentCommissionAmount } from "./commissionPlanUtils";
import {
  resolveInstallmentInvoicedTuition,
  toInputDate,
} from "./commissionInstallmentUtils";
import { roundMoney } from "./tuitionFeeSyncUtils";

const MONEY_EPS = 0.009;

function toNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

export function installmentRowTuition(row = {}) {
  return Math.max(toNumber(row.tuitionAmount ?? row.tuition_amount), 0);
}

/**
 * Rows with any invoice linkage or invoiced tuition are left unchanged.
 */
export function isInstallmentTuitionRebalanceable(row = {}) {
  const status = String(row?.status || "").toLowerCase();
  if (status === "paid" || status === "cancelled" || status === "void") {
    return false;
  }
  if (row?.financeInvoiceId || row?.invoiceId) return false;
  const allocs = row.financeAllocations ?? row.finance_allocations ?? [];
  if (Array.isArray(allocs) && allocs.length > 0) return false;
  if (resolveInstallmentInvoicedTuition(row) > MONEY_EPS) return false;
  return true;
}

function distributeProportional(total, weights) {
  const budget = Math.max(roundMoney(total), 0);
  const list = Array.isArray(weights) ? weights : [];
  if (!list.length) return [];
  const weightSum = list.reduce((sum, w) => sum + Math.max(toNumber(w), 0), 0);
  if (weightSum <= MONEY_EPS) {
    const even = roundMoney(budget / list.length);
    const parts = list.map(() => even);
    const sumExceptLast = roundMoney(even * (list.length - 1));
    parts[list.length - 1] = roundMoney(budget - sumExceptLast);
    return parts;
  }

  const parts = [];
  let allocated = 0;
  for (let i = 0; i < list.length - 1; i += 1) {
    const share = roundMoney(
      (budget * Math.max(toNumber(list[i]), 0)) / weightSum,
    );
    parts.push(share);
    allocated = roundMoney(allocated + share);
  }
  parts.push(roundMoney(budget - allocated));
  return parts;
}

export function buildInstallmentTuitionUpdatePayload(row, newTuition, plan = {}) {
  const tuitionAmount = roundMoney(Math.max(toNumber(newTuition), 0));
  const rate = toNumber(plan.commissionRate ?? plan.commission_rate);
  const gstType = plan.commissionGstType ?? plan.commission_gst_type ?? "none";
  const commissionType = plan.commissionType ?? plan.commission_type ?? "percentage";
  const planTuition = toNumber(
    plan.commissionableTuitionFee ??
      plan.commissionable_tuition_fee ??
      plan.tuitionFee ??
      plan.tuition_fee,
  );
  const finalCommission =
    resolveInstallmentCommissionAmount({
      tuitionFee: tuitionAmount,
      planTuitionFee: planTuition,
      commissionType,
      commissionRate: rate,
      commissionGstType: gstType,
    }) || 0;

  const dueRaw = row.dueDate ?? row.due_date;
  const dueDate = toInputDate(dueRaw) || dueRaw || null;

  return {
    due_date: dueDate,
    tuition_amount: tuitionAmount,
    student_paid_tuition: roundMoney(
      toNumber(row.studentPaidTuition ?? row.student_paid_tuition),
    ),
    commission_rate: rate,
    commission_amount: roundMoney(finalCommission),
    commission_gst_type: gstType,
    invoice_amount: roundMoney(finalCommission),
    remarks: row.remarks ?? row.note ?? null,
  };
}

/**
 * Compute new tuition for non-invoiced installments so their total matches
 * (newPlanCommissionable − locked invoiced tuition).
 */
export function buildPendingInstallmentTuitionRebalance(
  installments = [],
  newPlanCommissionable,
) {
  const rows = Array.isArray(installments) ? installments : [];
  const planCap = roundMoney(newPlanCommissionable);
  if (planCap < 0) {
    return { ok: false, reason: "invalid_cap", updates: [] };
  }

  let lockedTuition = 0;
  const adjustable = [];

  rows.forEach((row) => {
    const tuition = installmentRowTuition(row);
    if (!isInstallmentTuitionRebalanceable(row)) {
      lockedTuition = roundMoney(lockedTuition + tuition);
      return;
    }
    if (!row?.id) return;
    adjustable.push({ row, currentTuition: tuition });
  });

  lockedTuition = roundMoney(lockedTuition);
  if (lockedTuition > planCap + MONEY_EPS) {
    return { ok: false, reason: "locked_exceeds_cap", updates: [] };
  }

  const flexibleBudget = roundMoney(planCap - lockedTuition);
  if (!adjustable.length) {
    return { ok: true, reason: "no_adjustable_rows", updates: [] };
  }

  const weights = adjustable.map((item) => item.currentTuition);
  const targets = distributeProportional(flexibleBudget, weights);

  const updates = adjustable
    .map((item, index) => ({
      row: item.row,
      previousTuition: item.currentTuition,
      nextTuition: targets[index] ?? 0,
    }))
    .filter(
      (item) =>
        Math.abs(item.nextTuition - item.previousTuition) > MONEY_EPS,
    );

  return { ok: true, reason: "ready", updates, flexibleBudget, lockedTuition };
}

export function mergeInstallmentTuitionUpdates(installments = [], updates = []) {
  if (!updates.length) return installments;
  const byId = new Map(
    updates.map((item) => [String(item.row.id), item.nextTuition]),
  );
  return (Array.isArray(installments) ? installments : []).map((row) => {
    const next = byId.get(String(row.id));
    if (next == null) return row;
    return {
      ...row,
      tuitionAmount: next,
      tuition_amount: next,
    };
  });
}

/**
 * Apply tuition rebalance via sequential API updates (two-phase when multiple rows).
 */
export async function applyPendingInstallmentTuitionRebalance({
  installments = [],
  newPlanCommissionable,
  plan = {},
  updateInstallment,
}) {
  const built = buildPendingInstallmentTuitionRebalance(
    installments,
    newPlanCommissionable,
  );
  if (!built.ok || !built.updates?.length) {
    return {
      applied: 0,
      installments,
      built,
    };
  }

  const updates = built.updates;
  const applyOne = async (row, amount) => {
    await updateInstallment(
      row.id,
      buildInstallmentTuitionUpdatePayload(row, amount, plan),
    );
  };

  if (updates.length === 1) {
    const [only] = updates;
    await applyOne(only.row, only.nextTuition);
  } else {
    for (const item of updates) {
      if (item.previousTuition > MONEY_EPS) {
        await applyOne(item.row, 0);
      }
    }
    for (const item of updates) {
      await applyOne(item.row, item.nextTuition);
    }
  }

  return {
    applied: updates.length,
    installments: mergeInstallmentTuitionUpdates(installments, updates),
    built,
  };
}
